import React, { useEffect, useState } from "react";
import { Box, Button, Typography, useMediaQuery, useTheme, Grid } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link } from "react-router-dom";
import { css } from "@emotion/css";
import Slider from "react-slick";
import InViewComponent from "../../../../components/InViewComponent/InViewComponent";
import HeroSkeleton from "../../../../components/Carousel/HeroSkeleton";
import { getPanelLugares } from "../../../../actions";
import RecordingsPage from "../RecordingsPage/RecordingsPage";

// Componente PlaceCard modificado para el slider
const PlaceCard = ({ id, name, artist, image, description, gradient }) => {
    const theme = useTheme();

    // Estilos para los gradientes
    const gradientStyles = {
        "gradient-1": {
            background: "linear-gradient(45deg, #ff9a9e 0%, #fad0c4 99%, #fad0c4 100%)",
        },
        "gradient-2": {
            background: "linear-gradient(to top, #a18cd1 0%, #fbc2eb 100%)",
        },
        "gradient-3": {
            background: "linear-gradient(120deg, #84fab0 0%, #8fd3f4 100%)",
        },
        "gradient-4": {
            background: "linear-gradient(to right, #ff758c, #ff7eb3)",
        },
        "gradient-5": {
            background: "linear-gradient(to right, #ffc3a0, #ffafbd)",
        },
        "gradient-6": {
            background: "linear-gradient(to right, #43e97b, #38f9d7)",
        },
        "gradient-7": {
            background: "linear-gradient(to right, #fa709a, #fee140)",
        },
        "gradient-8": {
            background: "linear-gradient(to right, #30cfd0, #330867)",
        },
        "gradient-9": {
            background: "linear-gradient(to right, #667eea, #764ba2)",
        },
        "gradient-10": {
            background: "linear-gradient(to right, #f6d365, #fda085)",
        },
    };

    return (
        <Box
            sx={{
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
                height: "300px",
                ...(!image && gradientStyles[gradient]), // Aplicamos el gradiente si no hay imagen
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                padding: "16px",
                boxShadow: theme.shadows[2],
                transition: "transform 0.3s ease",
                "&:hover": {
                    transform: "scale(1.03)",
                    boxShadow: theme.shadows[4],
                },
            }}
        >
            {image && (
                <img
                    src={image}
                    alt={name}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                    }}
                />
            )}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    color: "white",
                    textShadow: "1px 1px 3px rgba(0,0,0,0.8)",
                }}
            >
                <Typography variant="h4" fontWeight="bold">
                    {name}
                </Typography>
                <Typography variant="h5">{artist}</Typography>
                {description && (
                    <Typography variant="body1" sx={{ marginTop: "10px" }}>
                        {description}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

// Componente para tarjeta de canción (más grande)
const SongCard = ({ position, name, artist, image, streams, gradient }) => {
    const theme = useTheme();

    // Estilos para los gradientes
    const gradientStyles = {
        "gradient-1": "linear-gradient(45deg, #ff9a9e 0%, #fad0c4 99%, #fad0c4 100%)",
        "gradient-2": "linear-gradient(to top, #a18cd1 0%, #fbc2eb 100%)",
        "gradient-3": "linear-gradient(120deg, #84fab0 0%, #8fd3f4 100%)",
        "gradient-4": "linear-gradient(to right, #ff758c, #ff7eb3)",
        "gradient-5": "linear-gradient(to right, #ffc3a0, #ffafbd)",
        "gradient-6": "linear-gradient(to right, #43e97b, #38f9d7)",
        "gradient-7": "linear-gradient(to right, #fa709a, #fee140)",
        "gradient-8": "linear-gradient(to right, #30cfd0, #330867)",
        "gradient-9": "linear-gradient(to right, #667eea, #764ba2)",
        "gradient-10": "linear-gradient(to right, #f6d365, #fda085)",
    };

    return (
        <Box
            sx={{
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
                height: "200px",
                background: image ? "transparent" : gradientStyles[gradient],
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "16px",
                boxShadow: theme.shadows[2],
                transition: "transform 0.3s ease",
                "&:hover": {
                    transform: "scale(1.03)",
                    boxShadow: theme.shadows[4],
                },
                color: "white",
            }}
        >
            {image && (
                <img
                    src={image}
                    alt={name}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                    }}
                />
            )}
            {image && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(0,0,0,0.5)",
                        zIndex: 1,
                    }}
                />
            )}

            <Box sx={{ position: "relative", zIndex: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                    #{position}
                </Typography>
            </Box>

            <Box sx={{ textAlign: "center", position: "relative", zIndex: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                    {name}
                </Typography>
                <Typography variant="subtitle1">{artist}</Typography>
            </Box>

            <Box sx={{ textAlign: "right", position: "relative", zIndex: 2 }}>
                <Typography variant="caption">{streams} streams</Typography>
            </Box>
        </Box>
    );
};

// Componente para tarjeta de artista (más grande)
const ArtistCard = ({ position, name, image, gradient }) => {
    const theme = useTheme();

    // Estilos para los gradientes
    const gradientStyles = {
        "gradient-1": "linear-gradient(45deg, #ff9a9e 0%, #fad0c4 99%, #fad0c4 100%)",
        "gradient-2": "linear-gradient(to top, #a18cd1 0%, #fbc2eb 100%)",
        "gradient-3": "linear-gradient(120deg, #84fab0 0%, #8fd3f4 100%)",
        "gradient-4": "linear-gradient(to right, #ff758c, #ff7eb3)",
        "gradient-5": "linear-gradient(to right, #ffc3a0, #ffafbd)",
        "gradient-6": "linear-gradient(to right, #43e97b, #38f9d7)",
        "gradient-7": "linear-gradient(to right, #fa709a, #fee140)",
        "gradient-8": "linear-gradient(to right, #30cfd0, #330867)",
        "gradient-9": "linear-gradient(to right, #667eea, #764ba2)",
        "gradient-10": "linear-gradient(to right, #f6d365, #fda085)",
    };

    return (
        <Box
            sx={{
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
                height: "200px",
                background: image ? "transparent" : gradientStyles[gradient],
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "16px",
                boxShadow: theme.shadows[2],
                transition: "transform 0.3s ease",
                "&:hover": {
                    transform: "scale(1.03)",
                    boxShadow: theme.shadows[4],
                },
                color: "white",
            }}
        >
            {image && (
                <img
                    src={image}
                    alt={name}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                    }}
                />
            )}
            {image && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(0,0,0,0.5)",
                        zIndex: 1,
                    }}
                />
            )}

            <Box sx={{ position: "relative", zIndex: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                    #{position}
                </Typography>
            </Box>

            <Box sx={{ textAlign: "center", position: "relative", zIndex: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                    {name}
                </Typography>
            </Box>
        </Box>
    );
};

// Componentes de flechas para el slider
const SlickArrowLeft = ({ show = true, currentSlide, slideCount, ...props }) => {
    if (!show) {
        return;
    }
    return (
        <Button
            {...props}
            color="primary"
            variant="contained"
            className={"slick-prev slick-arrow" + (currentSlide === 0 ? " slick-disabled" : "")}
            aria-hidden="true"
            aria-disabled={currentSlide === 0 ? true : false}
            sx={{
                zIndex: 20,
                "&:before": {
                    display: "none",
                },
                display: "flex !important",
                position: "absolute",
                minWidth: 0,
                width: "35px",
                height: "35px",
                borderRadius: "100%",
                marginLeft: { md: "-4px" },
            }}
        >
            <ChevronLeftIcon
                sx={{
                    color: `#222 !important`,
                    marginLeft: "-3px",
                }}
            />
        </Button>
    );
};

const SlickArrowRight = ({ show = true, currentSlide, slideCount, ...props }) => {
    if (!show) {
        return;
    }
    return (
        <Button
            {...props}
            className={
                "slick-next slick-arrow" + (currentSlide === slideCount - 1 ? " slick-disabled" : "")
            }
            aria-hidden="true"
            aria-disabled={currentSlide === slideCount - 1 ? true : false}
            color="primary"
            variant="contained"
            sx={{
                zIndex: 20,
                "&:before": {
                    display: "none",
                },
                display: "flex !important",
                position: "absolute",
                minWidth: 0,
                width: "35px",
                height: "35px",
                borderRadius: "100%",
            }}
        >
            <ChevronRightIcon
                sx={{
                    color: `#222 !important`,
                }}
            />
        </Button>
    );
};

const ChartsHome = () => {
    const theme = useTheme();
    const isScreenSM = useMediaQuery(theme.breakpoints.only("sm"));
    const isScreenXS = useMediaQuery(theme.breakpoints.only("xs"));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [songs, setSongs] = useState([]);
    const [artists, setArtists] = useState([]);
    const [featuredTracks, setFeaturedTracks] = useState([]);
    const [showAllSongs, setShowAllSongs] = useState(false);
    const [showAllArtists, setShowAllArtists] = useState(false);

    // Función para obtener la URL de la imagen
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith("http")) return imagePath;
        return `https://strapi.remiscuba.com${imagePath}`;
    };

    // Función para generar gradientes aleatorios
    const getRandomGradient = () => {
        const randomNum = Math.floor(Math.random() * 10) + 1;
        return `gradient-${randomNum}`;
    };

    // Obtener los datos
    useEffect(() => {
        const fetchChartData = async () => {
            try {
                setLoading(true);

                // Obtener canciones (top 100)
                const songsResponse = await getPanelLugares({
                    page: 1,
                    pageSize: 100,
                    type: "songs",
                });

                // Obtener artistas (top 100)
                const artistsResponse = await getPanelLugares({
                    page: 1,
                    pageSize: 100,
                    type: "artists",
                });

                // Obtener canciones destacadas para el slider
                const featuredResponse = await getPanelLugares({
                    page: 1,
                    pageSize: 10,
                    featured: true,
                });

                if (songsResponse && songsResponse.data) {
                    const formattedSongs = songsResponse.data.map((item, index) => ({
                        id: item.id,
                        name: item.attributes.name,
                        artist: item.attributes.artist,
                        image: getImageUrl(item.attributes.image),
                        position: item.attributes.position || index + 1, // Si no viene position, usa el índice + 1
                        streams: item.attributes.streams,
                        gradient: getRandomGradient(),
                    }));
                    console.log("songsResponse", songsResponse);
                    setSongs(formattedSongs);
                }

                if (artistsResponse && artistsResponse.data) {
                    const formattedArtists = artistsResponse.data.map((item, index) => ({
                        id: item.id,
                        name: item.attributes.artist,
                        image: getImageUrl(item.attributes.image),
                        position: item.attributes.position || index + 1, // Si no viene position, usa el índice + 1
                        gradient: getRandomGradient(),
                    }));
                    setArtists(formattedArtists);
                }

                if (featuredResponse && featuredResponse.data) {
                    const formattedFeatured = featuredResponse.data.map((item, index) => ({
                        id: item.id,
                        name: item.attributes.name,
                        artist: item.attributes.artist,
                        image: getImageUrl(item.attributes.image),
                        description: item.attributes.description,
                        position: item.attributes.position || index + 1, // Si no viene position, usa el índice + 1
                        gradient: getRandomGradient(),
                    }));
                    console.log("featuredResponse", featuredResponse);
                    setFeaturedTracks(formattedFeatured);
                }
            } catch (err) {
                console.error("Error fetching chart data:", err);
                setError("Error al cargar los charts. Por favor intente nuevamente.");
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();

        
    }, []);


    // Configuración del slider para canciones destacadas
    const getSliderSettings = () => {
        return {
            dots: false,
            infinite: featuredTracks && featuredTracks.length > 10,
            speed: 500,
            autoplay: false,
            centerMode: false, // Desactiva el centerMode para mostrar múltiples slides
            slidesToShow: 5, // Muestra 10 slides a la vez
            slidesToScroll: 10, // Se desplaza 10 slides cada vez
            prevArrow: <SlickArrowLeft />,
            nextArrow: <SlickArrowRight />,
            responsive: [
                {
                    breakpoint: 1500,
                    settings: {
                        slidesToShow: 4,
                        slidesToScroll: 4,
                    },
                },
                {
                    breakpoint: 1200,
                    settings: {
                        slidesToShow: 4,
                        slidesToScroll: 4,
                    },
                },
                {
                    breakpoint: 900,
                    settings: {
                        slidesToShow: 3,
                        slidesToScroll: 3,
                    },
                },
                {
                    breakpoint: 700,
                    settings: {
                        slidesToShow: 2,
                        slidesToScroll: 2,
                    },
                },
                {
                    breakpoint: 500,
                    settings: {
                        slidesToShow: 2,
                        slidesToScroll: 2,
                    },
                },
                {
                    breakpoint: 400,
                    settings: {
                        slidesToShow: 1,
                        slidesToScroll: 1,
                    },
                },
            ],
        };
    };

    if (loading) {
        return (
            <Box
                sx={{
                    padding: { xl: "0 5rem", lg: "0 4rem", xs: "0 1.5rem" },
                    marginTop: { xs: "150px", lg: "100px" },
                    marginBottom: "40px",
                }}
            >
                <HeroSkeleton />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    padding: { xl: "0 5rem", lg: "0 4rem", xs: "0 1.5rem" },
                    marginTop: { xs: "150px", lg: "100px" },
                    textAlign: "center",
                }}
            >
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                gap: "20px",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                padding: {
                    xl: "0 5rem",
                    lg: "0 4rem",
                    xs: "0 1.5rem",
                },
                marginTop: { xs: "150px", lg: "100px" },
            }}
        >
            {/* Encabezado Alta-Voz Chart */}
            <Typography
                component="h1"
                variant="h3"
                sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.8rem", lg: "3rem" },
                }}
            >
                Alta-Voz Chart
            </Typography>

            {/* Slider de canciones destacadas */}
            <Box sx={{ marginBottom: "40px" }}>
                <Slider {...getSliderSettings()}>
                    {featuredTracks.map((track) => (
                        <Box key={track.id} sx={{ padding: "0 10px" }}>
                            <PlaceCard
                                name={track.name}
                                artist={track.artist}
                                image={track.image}
                                description={track.description}
                                gradient={track.gradient}
                            />
                        </Box>
                    ))}
                </Slider>
            </Box>

            {/* Top semanal de canciones */}
            <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ marginBottom: "20px" }}>
                    Top semanal de canciones
                </Typography>

                <Grid container spacing={2}>
                    {(showAllSongs ? songs : songs.slice(0, 30)).map((song) => (
                        <Grid item xs={12} sm={6} md={4} key={song.id}>
                            <SongCard
                                position={song.position}
                                name={song.name}
                                artist={song.artist}
                                image={song.image}
                                streams={song.streams}
                                gradient={song.gradient}
                            />
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                    <Button
                        onClick={() => setShowAllSongs(!showAllSongs)}
                        className={css({
                            textDecoration: "none",
                            color: theme.palette.primary.main,
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                        })}
                    >
                        {showAllSongs ? "Mostrar menos" : "Seguir viendo → Top 100 de canciones"}
                    </Button>
                </Box>
            </Box>

            {/* Top semanal de artistas */}
            <Box sx={{ marginTop: "40px" }}>
                <Typography variant="h4" fontWeight="bold" sx={{ marginBottom: "20px" }}>
                    Top semanal de artistas
                </Typography>

                <Grid container spacing={2}>
                    {(showAllArtists ? artists : artists.slice(0, 30)).map((artist) => (
                        <Grid item xs={6} sm={4} md={3} lg={2.4} key={artist.id}>
                            <ArtistCard
                                position={artist.position}
                                name={artist.name}
                                image={artist.image}
                                gradient={artist.gradient}
                            />
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                    <Button
                        onClick={() => setShowAllArtists(!showAllArtists)}
                        className={css({
                            textDecoration: "none",
                            color: theme.palette.primary.main,
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                        })}
                    >
                        {showAllArtists ? "Mostrar menos" : "Seguir viendo → Top 100 de artistas"}
                    </Button>
                </Box>
            </Box>
            <Box sx={{ marginTop: "40px" }}>
                <Typography variant="h4" fontWeight="bold" sx={{ marginBottom: "20px" }}>
                    Noticias
                </Typography>

                <RecordingsPage />

                <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                    <Button
                        component={Link}
                        to="/noticias/grabaciones-lanzamientos"
                        className={css({
                            textDecoration: "none",
                            color: theme.palette.primary.main,
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                        })}
                    >
                        Seguir viendo
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ChartsHome;
